import type { ResourceWithSlug } from "./ResourceWithSlug";
import type { NamedResource } from "./namedResource";
import type { SensitiveValue } from "./propertyValueResource";
import type { IIdName } from "./resource";
import type { SpaceScopedResource } from "./spaceScopedResource";
import type { TenantedDeploymentMode } from "./tenantedDeploymentMode";
export enum AccountType {
    None = "None",
    UsernamePassword = "UsernamePassword",
    Token = "Token",
    SshKeyPair = "SshKeyPair",
    AzureSubscription = "AzureSubscription",
    AzureServicePrincipal = "AzureServicePrincipal",
    AzureManagementCertificate = "AzureManagementCertificate",
    AzureOidc = "AzureOidc",
    AmazonWebServicesAccount = "AmazonWebServicesAccount",
    AmazonWebServicesOidcAccount = "AmazonWebServicesOidcAccount",
    GoogleCloudAccount = "GoogleCloudAccount",
    GenericOidcAccount = "GenericOidcAccount"
}
interface AccountResourceLinks {
    Self: string;
    Usages: string;
}
export interface BaseAccountResource extends IIdName, ResourceWithSlug {
    Name: string;
    Description: string;
    AccountType: AccountType;
}
export interface AccountResource extends BaseAccountResource, NamedResource<AccountResourceLinks>, SpaceScopedResource {
    EnvironmentIds: string[];
    TenantIds: string[];
    TenantTags: string[];
    TenantedDeploymentParticipation: TenantedDeploymentMode;
}
export interface AzureServicePrincipalAccountResource extends AccountResource {
    SubscriptionNumber: string;
    ClientId: string;
    TenantId: string;
    Password: SensitiveValue;
    AzureEnvironment: string;
    ResourceManagementEndpointBaseUri: string;
    ActiveDirectoryEndpointBaseUri: string;
}
export interface AzureOidcAccountResource extends AccountResource {
    SubscriptionNumber: string;
    ClientId: string;
    TenantId: string;
    AzureEnvironment: string;
    TenantedSubjectGeneration: boolean;
    ResourceManagementEndpointBaseUri: string;
    ActiveDirectoryEndpointBaseUri: string;
    DeploymentSubjectKeys: string[];
    HealthCheckSubjectKeys: string[];
    AccountTestSubjectKeys: string[];
    Audience: string;
    CustomClaims: Record<string, string>;
}
export interface AzureSubscriptionAccountResource extends AccountResource {
    SubscriptionNumber: string;
    CertificateBytes: SensitiveValue;
    CertificateThumbprint: string;
    AzureEnvironment: string;
    ServiceManagementEndpointBaseUri: string;
    ServiceManagementEndpointSuffix: string;
}
export interface GenericOidcAccountResource extends AccountResource {
    TenantedSubjectGeneration: boolean;
    DeploymentSubjectKeys: string[];
    HealthCheckSubjectKeys: string[];
    AccountTestSubjectKeys: string[];
    Audience: string;
    CustomClaims: Record<string, string>;
}
export interface SshKeyPairAccountResource extends AccountResource {
    Username: string;
    PrivateKeyFile: SensitiveValue;
    PrivateKeyPassphrase: SensitiveValue;
}
export interface UsernamePasswordAccountResource extends AccountResource {
    Username: string;
    Password: SensitiveValue;
}
export interface TokenAccountResource extends AccountResource {
    Token: SensitiveValue;
}
export interface AmazonWebServicesAccessKeyAccountResource extends AccountResource {
    AccessKey: string;
    SecretKey: SensitiveValue;
}
export interface AmazonWebServicesOidcAccountResource extends AccountResource {
    RoleArn: string;
    SessionDuration: string;
    DeploymentSubjectKeys: string[];
    HealthCheckSubjectKeys: string[];
    AccountTestSubjectKeys: string[];
    CustomClaims: Record<string, string>;
}
export interface GoogleCloudAccountResource extends AccountResource {
    JsonKey: SensitiveValue;
}
